import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import UserImportModal from "./user-import-modal";
import {
  adminUserDeleteAction,
  adminUserUpdateStatusAction,
  adminUsersListAction
} from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function titleCase(value) {
  if (value === undefined || value === null) return "—";
  const s = String(value).trim();
  if (!s || s === "—") return "—";
  if (s.includes("@")) return s;
  return s
    .replace(/[_]+/g, " ")
    .split(/(\s+)/)
    .map((part) => {
      if (!part.trim()) return part;
      return part
        .split("-")
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
        .join("-");
    })
    .join("");
}

function userName(u) {
  if (!u) return "—";
  if (u.name) return titleCase(u.name);
  const first = u.first_name ?? u.firstName ?? "";
  const last = u.last_name ?? u.lastName ?? "";
  const joined = `${first} ${last}`.trim();
  if (joined) return titleCase(joined);
  if (u.username) return titleCase(u.username);
  if (u.email) return String(u.email);
  return "—";
}

function formatPurchasedCourses(u) {
  if (Array.isArray(u?.courses) && u.courses.length) {
    const titles = u.courses
      .map((c) => (typeof c === "string" ? c : c?.title))
      .map((t) => String(t || "").trim())
      .filter(Boolean);
    return titles.length ? titleCase(titles.join(", ")) : "—";
  }
  if (typeof u?.courses === "string" && u.courses.trim()) return titleCase(u.courses);
  return "—";
}

function getActiveStatus(u) {
  if (!u) return null;
  if (u.is_active !== undefined && u.is_active !== null) return Number(u.is_active) ? 1 : 0;
  if (typeof u.status === "string") {
    const s = u.status.toLowerCase();
    if (s === "active") return 1;
    if (s === "inactive") return 0;
  }
  return null;
}

export default function Users() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });
  const [updatingIds, setUpdatingIds] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [query, status]);

  useEffect(() => {
    let cancelled = false;
    const statusParam =
      status === "Active" ? "active" : status === "Inactive" ? "inactive" : undefined;
    const searchParam = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminUsersListAction({
          page,
          per_page: perPage,
          search: searchParam,
          status: statusParam
        });

        if (cancelled) return;
        if (!res?.success) {
          setUsers([]);
          setMeta({ total: 0, page, per_page: perPage });
          setError(res?.message || res?.msg || "Failed to load users.");
          return;
        }

        setUsers(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setUsers([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load users.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, status, reloadNonce]);

  const rows = useMemo(() => {
    return users.map((u) => {
      const isActive = getActiveStatus(u);
      const statusLabel = isActive === null ? "—" : isActive ? "Active" : "Inactive";
      const phone = u?.phone || u?.mobile || u?.contact || "—";
      const courses = formatPurchasedCourses(u);
      const registered = formatDate(u?.created_at || u?.registeredAt || u?.createdAt);
      return {
        raw: u,
        id: u?.id ?? u?._id ?? `${u?.email || "user"}-${userName(u)}`,
        name: userName(u),
        email: u?.email || "—",
        phone,
        registered,
        courses,
        statusLabel,
        isActive
      };
    });
  }, [users]);

  const total = meta?.total ? Number(meta.total) : rows.length;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && rows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && rows.length ? start + rows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const toggleUserStatus = async (row) => {
    if (!row?.id) return;
    const current = row.isActive;
    if (current === null) return;
    const next = current ? 0 : 1;

    setUpdatingIds((prev) => {
      const n = new Set(prev);
      n.add(row.id);
      return n;
    });
    setError("");
    try {
      const res = await adminUserUpdateStatusAction(row.id, next ? 1 : 0);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Failed to update status.");
      setUsers((prev) =>
        prev.map((u) => (String(u?.id) === String(row.id) ? { ...u, is_active: next } : u))
      );
      toast.success(`User ${next ? "activated" : "deactivated"}`, { toastId: "user-status" });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to update status.");
      toast.error("Status update failed", { toastId: "user-status" });
    } finally {
      setUpdatingIds((prev) => {
        const n = new Set(prev);
        n.delete(row.id);
        return n;
      });
    }
  };

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    setError("");
    try {
      const res = await adminUserDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Delete failed");
      toast.success("User deleted", { toastId: "user-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Delete failed";
      setError(msg);
      toast.error(msg, { toastId: "user-delete" });
    } finally {
      setDeleting(false);
    }
  };

  const clearAndReload = () => {
    setQuery("");
    setStatus("All");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  const exportExcel = async () => {
    setExporting(true);
    setError("");
    try {
      const statusParam =
        status === "Active" ? "active" : status === "Inactive" ? "inactive" : undefined;
      const searchParam = query.trim() ? query.trim() : undefined;

      const all = [];
      const exportPerPage = 200;
      let currentPage = 1;
      let total = null;

      while (true) {
        const res = await adminUsersListAction({
          page: currentPage,
          per_page: exportPerPage,
          search: searchParam,
          status: statusParam
        });

        if (!res?.success) throw new Error(res?.message || res?.msg || "Failed to export users.");

        const batch = Array.isArray(res.data) ? res.data : [];
        all.push(...batch);

        if (total === null) total = Number(res?.meta?.total || all.length);
        if (all.length >= total) break;
        if (batch.length === 0) break;
        currentPage += 1;
      }

      const toRow = (u) => {
        const createdAt = u?.created_at || u?.createdAt || u?.created || null;
        let registered = "";
        if (createdAt) {
          const d = new Date(createdAt);
          registered = Number.isNaN(d.getTime()) ? String(createdAt) : d.toISOString().slice(0, 10);
        }
        const isActive =
          u?.is_active !== undefined && u?.is_active !== null ? (Number(u.is_active) ? "Active" : "Inactive") : "";
        return {
          Name: titleCase(u?.name || u?.username || ""),
          Email: u?.email || "",
          Phone: u?.phone || "",
          Province: u?.province || "",
          "Relationship To Child": u?.relationship_to_child || "",
          "Adult Age": u?.adult_age ?? "",
          Status: isActive,
          Registered: registered
        };
      };

      const worksheet = XLSX.utils.json_to_sheet(all.map(toRow));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

      const date = new Date().toISOString().slice(0, 10);
      const filename = `users-${date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (e) {
      setError(e?.message || "Failed to export users.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Users data unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name, email..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            {["All", "Active", "Inactive"].map((v) => (
              <button
                key={v}
                className={`filter-btn ${status === v ? "active" : ""}`}
                onClick={() => setStatus(v)}
                type="button"
              >
                {v}
              </button>
            ))}
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn btn-outline" type="button" onClick={() => setImportOpen(true)} disabled={loading}>
            Import Excel
          </button>
          <button className="btn btn-outline" type="button" onClick={exportExcel} disabled={loading || exporting}>
            {exporting ? "Exporting…" : "📥 Export Excel"}
          </button>
          <Link to="/users/new" className="btn btn-primary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add New User
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Registered</th>
                <th>Courses</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : rows.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.name}</strong>
                  </td>
                  <td>{u.email}</td>
                  <td>{u.phone}</td>
                  <td>{u.registered}</td>
                  <td>{u.courses}</td>
                  <td>
                    <span
                      className={`badge ${
                        u.statusLabel === "Active" ? "badge-success" : u.statusLabel === "Inactive" ? "badge-danger" : "badge-gray"
                      }`}
                    >
                      {u.statusLabel}
                    </span>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <Link to={`/users/${u.id}`} className="action-link">
                        View
                      </Link>
                      <Link to={`/users/${u.id}/edit`} className="action-link">
                        Edit
                      </Link>
                      <button
                        type="button"
                        className={`action-link ${u.statusLabel === "Active" ? "text-danger" : "text-success"}`}
                        onClick={() => toggleUserStatus(u)}
                        disabled={updatingIds.has(u.id) || u.isActive === null}
                        style={{ background: "transparent", border: "none", padding: 0, opacity: updatingIds.has(u.id) ? 0.6 : 1 }}
                      >
                        {updatingIds.has(u.id)
                          ? "Updating…"
                          : u.statusLabel === "Active"
                            ? "Deactivate"
                            : u.statusLabel === "Inactive"
                              ? "Activate"
                              : "—"}
                      </button>
                      <button
                        type="button"
                        className="action-link text-danger"
                        onClick={() => openDelete(u)}
                        disabled={updatingIds.has(u.id) || deleting}
                        style={{ background: "transparent", border: "none", padding: 0, color: "var(--danger)" }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No users match your filters.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <span>
            Showing {start}-{end} of {total} Users
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              aria-label="First page"
            >
              {"‹‹"}
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              ‹
            </button>
            {pageButtons.map((p) => (
              <button
                key={p}
                className={p === page ? "active" : ""}
                type="button"
                onClick={() => setPage(p)}
                disabled={loading}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              aria-label="Last page"
            >
              {"››"}
            </button>
          </div>
        </div>
      </div>

      <UserImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => setReloadNonce((n) => n + 1)}
      />

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="user"
        itemName={deleteRow?.name || deleteRow?.email || "User"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}

